/**
 * Match Pulse™ — encuestas comerciales discretas durante el partido.
 *
 * Reglas duras:
 *  • Máximo 1 encuesta en el primer tiempo (1H)
 *  • Máximo 1 encuesta en el medio tiempo (HT)
 *  • Máximo 1 encuesta en el segundo tiempo (2H)
 *  • Máximo TOTAL: 3 por partido.
 *  • Si el usuario cierra una encuesta, NO se vuelve a mostrar el resto del partido.
 *  • Estado clave por user_id + match_id + session_id.
 *
 * Disparadores:
 *  1. Primer gol del partido → +15s → "¿Qué estás tomando para celebrar?"
 *  2. Minuto 30 → "¿Qué estás comiendo?"
 *  3. Minuto 70 → "¿Qué promoción te gustaría recibir?"
 *  4. Minuto 85 → "¿Quién será el MVP?" (opciones dinámicas desde caller).
 *
 *  Prioridad: si hubo gol en 1H, el gol consume el slot 1H. Si no, minuto 30 lo consume.
 *  Slot HT y 2H se evalúan independientes.
 */

export type MatchPulseTrigger =
  | "first_goal"
  | "minute_30"
  | "minute_70"
  | "minute_85_mvp"
  // Fan Data Pulse In-Game™ — auto-triggers in key minutes (Phase 3).
  | "minute_15"
  | "minute_60"
  | "minute_75"
  | "minute_90";
export type MatchHalf = "1H" | "HT" | "2H";

export interface MatchPulseQuestion {
  id: string;
  trigger: MatchPulseTrigger;
  half: MatchHalf;
  minute: number;
  question: string;
  options: string[];
  sponsor?: string;
  category: "consumption" | "promo_preference" | "brand_affinity" | "behavior" | "intent" | "prediction";
  /** If true, the user may pick multiple options. Submission sends a JSON array. */
  multiSelect?: boolean;
}

const STORAGE_KEY = "vs:match_pulse:v1";

interface MatchState {
  halvesShown: MatchHalf[];   // ["1H","HT","2H"]
  totalShown: number;
  totalAnswered: number;
  /** Legacy hard-close flag. Kept for migration; new flow uses cooldownUntil. */
  closed?: boolean;
  /** Epoch ms until which no new pulses should be shown after the user dismissed one. */
  cooldownUntil?: number | null;
  /** Ids the user has already answered — never re-show the same question. */
  answeredIds?: string[];
  lastTrigger?: MatchPulseTrigger;
  lastMinute?: number;
}
type Store = Record<string, MatchState>;

function readStore(): Store {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch { return {}; }
}
function writeStore(s: Store) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}
function keyFor(matchId: string, userOrSession: string) {
  return `${matchId}::${userOrSession}`;
}

export function getMatchState(matchId: string, userOrSession: string): MatchState {
  const s = readStore();
  return s[keyFor(matchId, userOrSession)] ?? {
    halvesShown: [], totalShown: 0, totalAnswered: 0,
    cooldownUntil: null, answeredIds: [],
  };
}
function updateMatchState(matchId: string, userOrSession: string, patch: Partial<MatchState>): MatchState {
  const s = readStore();
  const k = keyFor(matchId, userOrSession);
  const next: MatchState = { ...getMatchState(matchId, userOrSession), ...patch };
  s[k] = next;
  writeStore(s);
  return next;
}

/** Sponsor rotation — pulled from a fixed roster (no IA). */
export const MATCH_PULSE_SPONSORS = [
  "Coca-Cola", "Pepsi", "Gatorade", "Visa", "Nike", "Betano", "Bet365",
] as const;

function sponsorFor(trigger: MatchPulseTrigger): string {
  // Deterministic per trigger so misma sesión = mismo sponsor (no random ads).
  const map: Record<MatchPulseTrigger, string> = {
    first_goal: "Coca-Cola",
    minute_30: "Pepsi",
    minute_70: "Visa",
    minute_85_mvp: "Gatorade",
    minute_15: "Coca-Cola",
    minute_60: "Pepsi",
    minute_75: "Visa",
    minute_90: "Gatorade",
  };
  return map[trigger];
}

export interface BuildQuestionArgs {
  trigger: MatchPulseTrigger;
  minute: number;
  mvpCandidates?: string[];
}

export function buildQuestion({ trigger, minute, mvpCandidates }: BuildQuestionArgs): MatchPulseQuestion | null {
  const half: MatchHalf = minute >= 46 ? "2H" : minute === 45 || minute === 0 ? "HT" : "1H";
  const sponsor = sponsorFor(trigger);
  switch (trigger) {
    case "first_goal":
      return {
        id: `mp_${trigger}_${minute}`,
        trigger, half, minute, sponsor,
        category: "consumption",
        question: "¿Qué estás tomando para celebrar?",
        options: ["Refresco", "Agua", "Cerveza", "Energética", "Café"],
      };
    case "minute_30":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "consumption",
        question: "¿Qué estás comiendo?",
        options: ["Pizza", "Hamburguesa", "Snacks", "Tacos", "Otro"],
      };
    case "minute_70":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "promo_preference",
        question: "¿Qué promoción te gustaría recibir?",
        options: ["Comida", "Bebida", "Streaming", "Retail", "Apuesta Gratis", "Experiencia VIP"],
        multiSelect: true,
      };
    case "minute_85_mvp": {
      if (!mvpCandidates || mvpCandidates.length === 0) return null; // sin mocks
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "prediction",
        question: "¿Quién será el MVP?",
        options: mvpCandidates.slice(0, 6),
      };
    }
    case "minute_15":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "behavior",
        question: "¿Dónde estás viendo el partido?",
        options: ["Casa", "Bar", "Reunión", "Trabajo", "Estadio", "Celular"],
      };
    case "minute_60":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "consumption",
        question: "¿Qué estás tomando ahora?",
        options: ["Refresco", "Agua", "Cerveza", "Energética", "Café", "Nada"],
      };
    case "minute_75":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "brand_affinity",
        question: "¿Qué marca prefieres para ver fútbol?",
        options: ["Coca-Cola", "Pepsi", "Gatorade", "Modelo", "Heineken", "Otra"],
        multiSelect: true,
      };
    case "minute_90":
      return {
        id: `mp_${trigger}`,
        trigger, half, minute, sponsor,
        category: "intent",
        question: "¿Compartirías una promo si te da puntos?",
        options: ["Sí", "No", "Depende del premio"],
      };
  }
}

export interface DecideArgs {
  matchId: string;
  userOrSession: string;
  minute: number;
  firstGoalMinute: number | null;        // null = sin goles aún
  secondsSinceFirstGoal: number | null;  // para el delay de 15s
  mvpCandidates?: string[];
  /** Fan Data Pulse In-Game™ — Phase 3 gating. */
  activeForMs?: number;       // tiempo activo del usuario en sesión (ms)
  suppressed?: boolean;       // gol / VAR / penalti / momento crítico en curso
}

/**
 * Decide qué pregunta mostrar AHORA, o null si nada debe mostrarse.
 * Respeta límite duro de 3 y un slot por mitad. Idempotente: hasta que el caller
 * llame a `markShown` o `markClosed`, este método puede devolver la misma pregunta.
 */
export function decideMatchPulse(args: DecideArgs): MatchPulseQuestion | null {
  const {
    matchId, userOrSession, minute, firstGoalMinute, secondsSinceFirstGoal,
    mvpCandidates, activeForMs, suppressed,
  } = args;
  const state = getMatchState(matchId, userOrSession);
  if (state.closed) return null; // legacy: respect historical permanent close
  // PR-UX-VIRAL-01: cooldown after user dismisses without answering.
  if (state.cooldownUntil && Date.now() < state.cooldownUntil) return null;
  if (state.totalShown >= 3) return null;
  // Phase 3: nunca interferir con goles, VAR, penales o jugadas críticas.
  if (suppressed) return null;
  // PR-UX-VIRAL-01: hard-block any non-MVP pulse from minute 85 onwards
  // (final tense minutes belong to the match, not to sponsors).
  // The MVP question is the single allowed pulse at 85+.
  const allowAt85Plus = (t: MatchPulseTrigger) => t === "minute_85_mvp";

  const currentHalf: MatchHalf = minute >= 46 ? "2H" : (minute === 45 ? "HT" : "1H");
  if (state.halvesShown.includes(currentHalf)) return null;

  // Fan Data Pulse In-Game™ — requiere >2 min activos.
  const userActive = (activeForMs ?? 0) >= 120_000;
  const answered = new Set(state.answeredIds ?? []);
  const guard = (q: MatchPulseQuestion | null): MatchPulseQuestion | null => {
    if (!q) return null;
    if (minute >= 85 && !allowAt85Plus(q.trigger)) return null;
    if (answered.has(q.id)) return null;
    return q;
  };

  // Slot 1H: gol > minuto 30.
  if (currentHalf === "1H") {
    if (firstGoalMinute !== null && firstGoalMinute <= minute && secondsSinceFirstGoal !== null && secondsSinceFirstGoal >= 15) {
      return guard(buildQuestion({ trigger: "first_goal", minute }));
    }
    if (minute >= 30) return guard(buildQuestion({ trigger: "minute_30", minute }));
    if (userActive && minute >= 15) return guard(buildQuestion({ trigger: "minute_15", minute }));
    return null;
  }
  // Slot 2H: minuto 70 → minuto 85 MVP.
  if (currentHalf === "2H") {
    if (userActive && minute >= 90) return guard(buildQuestion({ trigger: "minute_90", minute }));
    if (minute >= 85 && mvpCandidates && mvpCandidates.length > 0) {
      return guard(buildQuestion({ trigger: "minute_85_mvp", minute, mvpCandidates }));
    }
    if (userActive && minute >= 75) return guard(buildQuestion({ trigger: "minute_75", minute }));
    if (minute >= 70) return guard(buildQuestion({ trigger: "minute_70", minute }));
    if (userActive && minute >= 60) return guard(buildQuestion({ trigger: "minute_60", minute }));
    return null;
  }
  // HT: reutilizamos minuto_30 si no se cumplió en 1H (consumption durante descanso).
  if (currentHalf === "HT") {
    return guard(buildQuestion({ trigger: "minute_30", minute: 45 }));
  }
  return null;
}

export function markShown(matchId: string, userOrSession: string, q: MatchPulseQuestion) {
  const s = getMatchState(matchId, userOrSession);
  if (s.halvesShown.includes(q.half)) return;
  updateMatchState(matchId, userOrSession, {
    halvesShown: [...s.halvesShown, q.half],
    totalShown: s.totalShown + 1,
    lastTrigger: q.trigger,
    lastMinute: q.minute,
    // Showing again clears any prior cooldown for the slot.
    cooldownUntil: null,
  });
}

export function markAnswered(matchId: string, userOrSession: string, questionId?: string) {
  const s = getMatchState(matchId, userOrSession);
  const prev = new Set(s.answeredIds ?? []);
  if (questionId) prev.add(questionId);
  updateMatchState(matchId, userOrSession, {
    totalAnswered: s.totalAnswered + 1,
    answeredIds: [...prev],
    cooldownUntil: null,
  });
}

/**
 * User dismissed without answering. Per PR-UX-VIRAL-01: do NOT permanently
 * ban future pulses — apply a soft cooldown (default 8 min) and let the
 * engine try again later in the same match.
 */
export function markClosed(
  matchId: string,
  userOrSession: string,
  opts?: { cooldownMs?: number },
) {
  const cooldownMs = opts?.cooldownMs ?? 8 * 60_000;
  updateMatchState(matchId, userOrSession, {
    cooldownUntil: Date.now() + cooldownMs,
  });
}

export function resetMatchPulse(matchId: string, userOrSession: string) {
  const s = readStore();
  delete s[keyFor(matchId, userOrSession)];
  writeStore(s);
}